import React from "react";

const brand = {
  color: "#F2C94C",
  darkText: "#111827",
  lightText: "#4B5563",
  surface: "#FFFFFF",
  border: "rgba(17,24,39,0.06)",
  shadow: "rgba(0,0,0,0.06)",
};

const features = [
  {
    title: "Secure Transactions",
    description:
      "Every deal is protected with bank-level encryption and escrow-backed payments.",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    Icon: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M6 10V8a6 6 0 0 1 12 0v2" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        <rect x="4" y="10" width="16" height="10" rx="2" stroke="#fff" strokeWidth="2" />
        <circle cx="12" cy="15" r="1.6" fill="#fff" />
      </svg>
    ),
  },
  {
    title: "Global Trading Network",
    description: "Connect with verified scrap buyers and sellers worldwide.",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    Icon: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" />
        <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" stroke="#fff" strokeWidth="2" />
      </svg>
    ),
  },
  {
    title: "Live Market Pricing",
    description:
      "Access real-time scrap prices and get instant alerts for better opportunities.",
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    Icon: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4 18l5-6 3 3 6-8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Seamless Logistics",
    description:
      "Book pickups or deliveries with trusted logistics partners in just a few clicks.",
    gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    Icon: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="7" width="10" height="8" rx="2" stroke="#fff" strokeWidth="2" />
        <path d="M13 9h5l3 3v3h-4" stroke="#fff" strokeWidth="2" />
        <circle cx="7" cy="18" r="2" fill="#fff" />
        <circle cx="18" cy="18" r="2" fill="#fff" />
      </svg>
    ),
  },
  {
    title: "Actionable Insights",
    description:
      "Leverage advanced analytics and reporting to maximize your profits.",
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    Icon: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="4" y="10" width="3" height="8" rx="1" fill="#fff" />
        <rect x="10" y="6" width="3" height="12" rx="1" fill="#fff" />
        <rect x="16" y="12" width="3" height="6" rx="1" fill="#fff" />
      </svg>
    ),
  },
  {
    title: "Smart Matchmaking",
    description:
      "AI-powered tools instantly connect you with the right buyers or sellers.",
    gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    Icon: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 3l2.5 5L20 9l-4 4 .8 5.5L12 16l-4.8 2.5L8 13 4 9l5.5-1L12 3z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export default function WhyChooseScrapTradeSection() {
  return (
    <section id="features" style={{ position: "relative", padding: "64px 0", background: "linear-gradient(180deg, rgba(255,247,224,.45) 0%, rgba(255,255,255,0) 55%)" }}>
      <style>{`
        .wc-container { max-width: 1120px; margin: 0 auto; padding: 0 16px; }
        .wc-container::before, .wc-container::after { content: ""; position:absolute; pointer-events:none; border-radius:9999px; filter:blur(40px); opacity:.15; }
        .wc-container::before { top:-20px; left:-40px; width:160px; height:160px; background: radial-gradient(100px 100px at 50% 50%, #FFE39B 0%, transparent 70%); }
        .wc-container::after { bottom:-30px; right:-30px; width:180px; height:180px; background: radial-gradient(110px 110px at 50% 50%, #FFD28C 0%, transparent 70%); }
        .wc-title { font-weight: 800; letter-spacing: -0.02em; font-size: clamp(28px, 4.2vw, 40px); text-align:center; color: ${brand.darkText}; }
        .wc-sub { margin-top: 12px; text-align:center; color: ${brand.lightText}; font-size: clamp(14px, 1.6vw, 16px); max-width: 860px; margin-left:auto; margin-right:auto; }
        .wc-grid { display:grid; grid-template-columns: 1fr; gap:16px; margin-top: 36px; }
        .wc-card { position:relative; overflow:hidden; border-radius:16px; background:${brand.surface}; border:1px solid ${brand.border}; box-shadow:0 10px 20px ${brand.shadow}; transition: transform .25s ease, box-shadow .25s ease; }
        .wc-card:hover { transform: translateY(-8px) scale(1.01); box-shadow:0 18px 30px rgba(0,0,0,.12); }
        .wc-bottom{ transform-origin:left; transform:scaleX(.3); opacity:.35; transition: transform .35s ease, opacity .2s ease; }
        .wc-card:hover .wc-bottom{ opacity:.2; transform:scaleX(1); }
        .wc-card:hover .wc-icon { transform: translateY(-2px) scale(1.03); animation: gradientMove 3s ease infinite alternate; }
        @keyframes gradientMove { 0% { background-position: 0% 50%; } 100% { background-position: 100% 50%; } }
        .wc-card-inner{ padding: clamp(16px, 3vw, 24px); }
        .wc-icon { height:clamp(40px,5vw,48px); width:clamp(40px,5vw,48px); border-radius:9999px; display:grid; place-items:center; box-shadow:0 6px 14px rgba(0,0,0,.12), inset 0 0 0 8px rgba(255,255,255,.9); transition: transform .25s ease, box-shadow .25s ease; background-size:200% 200%; }
        .wc-name{ margin-top:12px; font-weight:700; font-size:18px; color:${brand.darkText}; }
        .wc-desc{ margin-top:8px; font-size:14px; line-height:1.6; color:${brand.lightText}; }
        .wc-brand { background: linear-gradient(135deg, ${brand.color}, #E9B949); -webkit-background-clip: text; background-clip:text; color: transparent; }
        @keyframes fadeUp { from { opacity:0; transform: translateY(12px) scale(.98);} to { opacity:1; transform: translateY(0) scale(1);} }
        @media (prefers-reduced-motion: reduce) { .wc-card { animation: none !important; } }
        @media (min-width: 640px) { .wc-grid { grid-template-columns: repeat(2, minmax(0,1fr)); gap:20px; } }
        @media (min-width: 1024px) { .wc-grid { grid-template-columns: repeat(3, minmax(0,1fr)); gap:24px; } }
      `}</style>
      <div className="wc-container">
        <h2 className="wc-title">
          Why Choose <span className="wc-brand">ScrapTrade</span>?
        </h2>
        <p className="wc-sub">
          A smarter, safer, and faster way to trade scrap globally — with
          cutting-edge tools designed for serious buyers and sellers.
        </p>

        <div className="wc-grid">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="wc-card"
              style={{ animation: `fadeUp .6s ease both`, animationDelay: `${i * 90}ms` }}
            >
              <div className="wc-card-inner">
                <div className="wc-icon" style={{ background: f.gradient }}>
                  <f.Icon />
                </div>
                <div className="wc-name">{f.title}</div>
                <div className="wc-desc">{f.description}</div>
              </div>
              <div className="wc-bottom" style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, background: f.gradient }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
